import * as chrono from "chrono-node";
import * as d3 from "d3";
import { DateLanguages, type DateParserArguments } from "./types";

const webkitEpoch = 11644476808000;
const webkitFactor = 1 / 1000;

function isStringOrNumber(a: unknown): a is number | string {
  return a !== undefined && (typeof a === "string" || (typeof a === "number" && !Number.isNaN(a)));
}

function suggestDate(timestamp: unknown): { argument: DateParserArguments; sample: Date } | undefined {
  if (!isStringOrNumber(timestamp)) {
    return undefined;
  }
  if (
    timestamp === "" ||
    timestamp === 0 ||
    timestamp === 1 ||
    (typeof timestamp === "string" && timestamp.length <= 6) ||
    (typeof timestamp === "string" && timestamp.length >= 30)
  ) {
    return undefined;
  }

  const suggestions = [
    // 2020-10-22T09:17:38.049Z
    { format: "%Y-%m-%dT%H:%M:%S.%L%Z" },
    // 2020-10-28 15:55:05.995000+00:00
    { format: "%Y-%m-%d %H:%M:%S.%f%Z" },
    // 2020-10-28 15:55:05
    { format: "%Y-%m-%d %H:%M:%S" },
    // Jan 21, 2020 10:55 AM
    { format: "%b %d, %Y %I:%M %p" },
    { iso: true },
    { factor: 1 / 1000 },
    { factor: 1 / 1000000 },
    { factor: 1 },
    { factor: 1000 },
    { factor: webkitFactor, epoch: webkitEpoch },
    { lang: "en" },
  ]
    .map((arg) => ({ argument: arg, date: processTime(timestamp, arg) }))
    .filter((d) => isValidDate(d.date))
    .sort((b, a) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));

  if (suggestions.length > 0) {
    return {
      argument: suggestions[0].argument,
      sample: suggestions[0].date ?? new Date(0),
    };
  }
  return undefined;
}

/**
 * Checks if a given date is a valid date between 1972 and the curent Date.
 *
 * @param {Date | undefined} date - The date object to be checked.
 * @returns {boolean} - True if the date is valid, false otherwise.
 */
function isValidDate(date: Date | undefined): boolean {
  return (
    date !== undefined &&
    date instanceof Date &&
    !Number.isNaN(date.getTime()) &&
    date.getTime() > 1000 * 60 * 60 * 24 * 365 * 2 &&
    date.getTime() < Date.now()
  );
}

function processTime(timestamp: number | string, argument: DateParserArguments): Date | undefined {
  if ("factor" in argument || "epoch" in argument) {
    try {
      const num = parseFloat(String(timestamp));

      if (num && !Number.isNaN(num)) {
        const f = argument.factor || 1;
        const e = argument.epoch || 0;
        return new Date(num * f - e);
      }
    } catch (e) {
      console.error(e);
    }
  } else if ("iso" in argument) {
    return new Date(timestamp);
  } else if ("lang" in argument) {
    const lang = argument.lang;
    const result = selectDateParser(lang)(String(timestamp));

    if (result !== null) {
      return result;
    }
  } else if ("pattern" in argument) {
    return d3.timeParse(argument.pattern)(String(timestamp)) ?? undefined;
  }
  return undefined;
}

/**
 * Selects the appropriate date parser function based on the given language.
 *
 * @param {DateLanguages} lang - The language to select the date parser for.
 * @returns {(timestamp: string) => Date | null} - The selected date parser function.
 */
function selectDateParser(lang: DateLanguages): (timestamp: string) => Date | null {
  switch (lang) {
    case DateLanguages.en:
      return chrono.en.parseDate;
    case DateLanguages.de:
      return chrono.de.parseDate;
    case DateLanguages.fr:
      return chrono.fr.parseDate;
    case DateLanguages.ja:
      return chrono.ja.parseDate;
    case DateLanguages.nl:
      return chrono.nl.parseDate;
    case DateLanguages.pt:
      return chrono.pt.parseDate;
    case DateLanguages.zh:
      return chrono.zh.parseDate;
    default:
      break;
  }
  return chrono.parseDate;
}

export { isValidDate, processTime, suggestDate };
